import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from '../Services/FAQAccordion';
import Seo from '../Seo';

const SolutionContingent = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);



  const faqs = [
    {
      q: "What defines a contingent workforce?",
      a: "A contingent workforce includes professionals engaged for project-driven, time-bound, or specialized delivery requirements rather than permanent headcount roles. This model gives organizations flexibility to scale capacity based on workload, transformation phases, and business priorities. It is especially effective when expertise is needed quickly in domains where long recruitment cycles are impractical. Contingent staffing also helps reduce fixed staffing commitments while preserving delivery momentum. When governed correctly, it becomes a strategic capability, not just a short-term hiring tactic."
    },
    {
      q: "How fast can you deploy specialized IT talent?",
      a: "Deployment speed depends on role complexity, location, and project constraints, but our model is designed for accelerated fulfillment. We use structured requirement intake, role calibration, and curated talent pipelines to minimize cycle time without compromising quality. For many specialized IT roles, shortlisting can be delivered quickly with validated profiles and domain-fit assessment. We then support interview orchestration and onboarding readiness to compress end-to-end time-to-productivity. This allows teams to maintain project velocity when critical resource gaps appear."
    },
    {
      q: "Do you handle the compliance and onboarding?",
      a: "Yes. Our contingent workforce model includes compliance and onboarding support as a standard part of engagement governance. We manage background checks, documentation workflows, engagement classification, and onboarding coordination to reduce administrative load on client teams. Access readiness and process handover are aligned with project timelines to avoid delays at start. We also maintain structured records and controls to support audit and operational visibility requirements. This lets internal teams focus on delivery outcomes instead of staffing administration complexity."
    },
    {
      q: "How do you maintain contractor quality across long programs?",
      a: "Quality is sustained through structured screening, role-fit validation, and ongoing performance governance throughout the engagement lifecycle. We align contractor onboarding with project expectations so delivery standards are clear from day one. Periodic feedback loops with hiring managers help identify improvement areas early and avoid performance drift. Where needed, we support replacement planning to protect project timelines without operational disruption. This creates a stable contingent model that balances speed, quality, and continuity."
    },
    {
      q: "Can contingent teams scale up or down during project changes?",
      a: "Yes, scalability is one of the core advantages of a contingent model when business priorities shift mid-cycle. We design workforce plans with phased capacity options so teams can expand or reduce resources with minimal friction. This is especially useful for transformation programs where demand changes by milestone, sprint, or release stage. Structured forecasting and communication cadence help prevent overstaffing while protecting critical delivery windows. The result is a more responsive workforce strategy aligned to real project conditions."
    }
  ];

  return (
    <div className="bg-white min-h-screen pt-4 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Strategic Staffing"
        description="Libsys strategic staffing — fast role fulfillment, compliance-first onboarding, scalable workforce solutions for project deadlines and operational demand spikes."
        path="/solutions/contingent"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="rounded-[2.5rem] border border-[#236086]/10 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-red-tagline mb-5">
                Strategic Staffing & Agile Talent
              </span>
              <h1 className="text-h1 text-primary mb-6">
                Scale at the Speed of Innovation with <span className="text-secondary">On-Demand</span> Talent
              </h1>
              <p className="text-slate-600 text-body leading-[1.8] font-medium mb-8 max-w-2xl">
                In a volatile market, agility is your greatest competitive advantage. Libsys provides a seamless bridge to a global pool of elite professionals, allowing you to scale your capacity on-demand. Whether you need a single specialized developer or an entire project team, our contingent workforce solutions ensure you have the right skills at the right time. We combine rapid sourcing, role-fit validation, and compliance-ready onboarding with rigorous delivery governance so your teams can scale quickly without losing operational control.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-10">
                {[
                  'Fast specialist deployment',
                  'Compliance & onboarding support',
                  'Scalable workforce planning',
                  'Project continuity controls',
                  'Vetted elite talent pipelines',
                  'Agile capacity management'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-small font-bold leading-relaxed flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-10 py-5 bg-primary hover:bg-secondary text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-xl active:scale-95 group"
                >
                  Schedule Staffing Review
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
              <div className="rounded-[2.5rem] border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                 <img
                  src="/contigent1.png?v=2"
                  alt="Strategic staffing simulation"
                  className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ STRATEGIC VALUE SECTION ═══════════════════════════════════════════ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-h2 text-primary mb-6">Staffing Built for Velocity</h2>
            <p className="text-slate-600 font-medium leading-relaxed">
              We eliminate the bottlenecks of traditional hiring, providing a frictionless path to the specialized expertise your projects require today.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "Talent Liquidity", desc: "Instantly access pre-vetted professionals in niche domains without the long lead times of permanent recruitment cycles." },
              { title: "Risk Governance", desc: "Our model includes full compliance management and onboarding oversight to protect your operational standards." },
              { title: "Variable Scale", desc: "Easily adjust team sizes in response to shifting project milestones, sprint demands, or seasonal business peaks." }
            ].map((val, i) => (
              <div key={i} className="p-10 rounded-[2rem] bg-[#e2edf5] border border-[#236086]/10 hover:shadow-xl transition-all">
                <h4 className="text-xl font-black text-primary mb-4">{val.title}</h4>
                <p className="text-slate-600 text-body leading-relaxed font-medium">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CORE VALUE ══════════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-tagline block mb-4">Delivery Governance</span>
              <h2 className="text-h2 text-primary mb-6">Scale Teams with Precision & Velocity</h2>
              <p className="text-slate-600 text-small mb-8 leading-[1.8] font-medium">
                Our staffing methodology is designed to translate your project requirements into high-performance delivery teams. We don't just provide resumes; we help you build an agile workforce that integrates seamlessly with your internal processes. By managing the end-to-end talent lifecycle—from rapid shortlist creation to compliance-ready onboarding—we ensure that your resource gaps never become project bottlenecks. Libsys stays engaged as your governance partner to ensure sustained delivery quality and capability alignment.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 mb-10">
                {[
                  { title: "Rapid Sourcing", desc: "Curated talent pipelines for high-velocity specialist deployment." },
                  { title: "Compliance Ready", desc: "Full documentation and engagement governance oversight." },
                  { title: "Process Sync", desc: "Seamless integration with your existing staffing workflows." },
                  { title: "Vetted Prowess", desc: "Rigorous technical and cultural fit validation before start." }
                ].map((item, i) => (
                  <div key={i} className="space-y-2.5">
                    <div className="text-secondary flex items-center gap-2">
                      <span className="font-black text-primary text-tiny uppercase tracking-widest">{item.title}</span>
                    </div>
                    <p className="text-slate-600 text-subtext leading-relaxed font-semibold">{item.desc}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-4">
                {[
                  'Fulfillment rates exceeding 95% on specialized delivery roles',
                  'Flexible ramp-up/down engagement models (1-20+ professionals)',
                  'Standardized onboarding to ensure immediate time-to-productivity'
                ].map((item) => (
                  <li key={item} className="text-slate-800 text-subtext font-bold leading-relaxed flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="rounded-[2.5rem] overflow-hidden border border-[#236086]/10 shadow-2xl bg-white p-2">
                 <img
                  src="/contigent2.png?v=2"
                  alt="Strategic contingent staffing operations"
                  className="w-full h-auto aspect-square lg:aspect-[4/3] object-cover rounded-[2.2rem]"
                  onError={(e) => { e.currentTarget.src = '/hero_tech_office.png'; }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ═══════════════════════════════════════════════════════ */}
      <section className="py-12 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-primary rounded-[3rem] p-8 lg:py-10 lg:px-12 relative overflow-hidden text-center lg:text-left">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="text-blue-100/60 text-tiny font-black uppercase tracking-[0.4em] mb-2 block">Deployment Ready</span>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                Strategic Staffing for a <span className="text-secondary">Borderless Enterprise</span>
              </h2>
              <p className="text-blue-100/80 text-lg mb-6 max-w-2xl font-medium">
                Get absolute agility and uncompromised quality delivered directly to your projects. Our contingent model ensures you maintain momentum without the overhead of permanent hiring.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href="tel:+16307991556"
                  className="px-10 py-5 bg-white text-primary font-black rounded-xl hover:bg-blue-50 transition-all flex items-center gap-3 uppercase tracking-widest text-subtext shadow-2xl active:scale-95"
                >
                  630-799-1556
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { k: 'Speed', v: 'Accelerated Deployment' },
                  { k: 'Model', v: 'Agile Resource Allocation' },
                  { k: 'Governance', v: 'Compliance Guaranteed' }
                ].map((item) => (
                  <div key={item.k} className="bg-white/10 backdrop-blur-md border border-white/10 p-4 rounded-2xl text-left">
                    <p className="text-blue-100/50 text-[10px] font-black uppercase tracking-widest">{item.k}</p>
                    <p className="text-white text-small font-bold mt-1">{item.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default SolutionContingent;
